import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useTheme
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import GetAppIcon from '@mui/icons-material/GetApp';
import ReplayIcon from '@mui/icons-material/Replay';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import { ImportHistory, ImportStatus } from '../../types/importHistory';

export interface ImportHistoryTableProps {
  /**
   * Liste des importations
   */
  imports: ImportHistory[];
  
  /**
   * Nombre total d'importations (pour la pagination)
   */
  total: number;
  
  /**
   * Callback appelé lorsqu'on clique sur "Voir les détails"
   */
  onViewDetails: (importId: string) => void;
  
  /**
   * Callback appelé lorsqu'on clique sur "Télécharger le rapport"
   */
  onDownloadReport: (importId: string, format: 'csv' | 'pdf') => void;
  
  /**
   * Callback appelé lorsqu'on clique sur "Réimporter"
   */
  onReimport?: (importId: string) => void;
  
  /**
   * Callback appelé lorsqu'on clique sur "Supprimer"
   */
  onDelete?: (importId: string) => void;
  
  /**
   * Titre du tableau
   * @default "Historique des importations"
   */
  title?: string;
  
  /**
   * Indique si le tableau est en cours de chargement
   * @default false
   */
  loading?: boolean;
  
  /**
   * Page actuelle (pour la pagination)
   * @default 0
   */
  page?: number;
  
  /**
   * Nombre d'éléments par page (pour la pagination)
   * @default 10
   */
  rowsPerPage?: number;
  
  /**
   * Callback appelé lorsqu'on change de page
   */
  onPageChange?: (page: number) => void;
  
  /**
   * Callback appelé lorsqu'on change le nombre d'éléments par page
   */
  onRowsPerPageChange?: (rowsPerPage: number) => void;
}

/**
 * Composant pour afficher le tableau d'historique des importations
 */
const ImportHistoryTable = ({
  imports,
  total,
  onViewDetails,
  onDownloadReport,
  onReimport,
  onDelete,
  title = "Historique des importations",
  loading = false,
  page = 0,
  rowsPerPage = 10,
  onPageChange,
  onRowsPerPageChange
}: ImportHistoryTableProps): React.ReactElement => {
  const theme = useTheme();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedImportId, setSelectedImportId] = useState<string | null>(null);
  
  // Gérer l'ouverture du menu d'actions
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, importId: string) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedImportId(importId);
  };
  
  // Gérer la fermeture du menu d'actions
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedImportId(null);
  };
  
  // Gérer le changement de page
  const handleChangePage = (_: unknown, newPage: number) => {
    if (onPageChange) {
      onPageChange(newPage);
    }
  };
  
  // Gérer le changement du nombre d'éléments par page
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onRowsPerPageChange) {
      onRowsPerPageChange(parseInt(event.target.value, 10));
    }
    if (onPageChange) {
      onPageChange(0);
    }
  };
  
  // Formater la date
  const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };
  
  // Obtenir le statut d'une importation
  const getStatusInfo = (status: ImportStatus) => {
    switch (status) {
      case 'complete':
        return { 
          label: 'Complète', 
          color: 'success' as const, 
          icon: <CheckCircleIcon fontSize="small" />
        };
      case 'partial':
        return { 
          label: 'Partielle', 
          color: 'warning' as const, 
          icon: <WarningIcon fontSize="small" />
        };
      case 'failed':
        return { 
          label: 'Échec', 
          color: 'error' as const, 
          icon: <ErrorIcon fontSize="small" />
        };
      default:
        return { 
          label: 'Inconnue', 
          color: 'default' as const, 
          icon: null
        };
    }
  };
  
  return (
    <Box sx={{ width: '100%', mb: 3 }}>
      <Paper sx={{ width: '100%', p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Fichier</TableCell>
                <TableCell>Fournisseur</TableCell>
                <TableCell align="center">Transactions</TableCell>
                <TableCell align="center">Réussies</TableCell>
                <TableCell align="center">Erreurs</TableCell>
                <TableCell align="center">Statut</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {imports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    {loading ? 'Chargement...' : 'Aucune importation trouvée'}
                  </TableCell>
                </TableRow>
              ) : (
                imports.map((importItem) => {
                  const statusInfo = getStatusInfo(importItem.status);
                  
                  return (
                    <TableRow 
                      key={importItem.id}
                      hover
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        backgroundColor: importItem.status === 'failed' 
                          ? theme.palette.error.light + '10' // 10% d'opacité
                          : undefined
                      }}
                    >
                      <TableCell>
                        {formatDate(importItem.importDate)}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={importItem.fileName}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                            {importItem.fileName}
                          </Typography>
                        </Tooltip>
                        <Typography variant="caption" color="text.secondary">
                          {(importItem.fileSize / 1024).toFixed(2)} Ko
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {importItem.supplierId ? (
                          importItem.summary.supplierName || 'Fournisseur inconnu'
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Sans fournisseur
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {importItem.summary.totalTransactions}
                      </TableCell>
                      <TableCell align="center">
                        <Typography 
                          variant="body2" 
                          color={importItem.summary.processedTransactions > 0 ? 'success.main' : 'text.secondary'}
                        >
                          {importItem.summary.processedTransactions}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {importItem.summary.errorsCount > 0 ? (
                          <Chip 
                            label={importItem.summary.errorsCount} 
                            color="error" 
                            size="small" 
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            0
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          icon={statusInfo.icon}
                          label={statusInfo.label} 
                          color={statusInfo.color} 
                          size="small" 
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Voir les détails">
                          <IconButton 
                            size="small" 
                            onClick={() => onViewDetails(importItem.id)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <IconButton 
                          size="small" 
                          onClick={(e) => handleMenuOpen(e, importItem.id)}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Lignes par page:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
        
        {/* Menu d'actions */}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem 
            onClick={() => {
              if (selectedImportId) {
                onDownloadReport(selectedImportId, 'csv');
                handleMenuClose();
              }
            }}
          >
            <ListItemIcon>
              <GetAppIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Télécharger CSV</ListItemText>
          </MenuItem>
          <MenuItem 
            onClick={() => {
              if (selectedImportId) {
                onDownloadReport(selectedImportId, 'pdf');
                handleMenuClose();
              }
            }}
          >
            <ListItemIcon>
              <GetAppIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Télécharger PDF</ListItemText>
          </MenuItem>
          {onReimport && (
            <MenuItem 
              onClick={() => {
                if (selectedImportId && onReimport) {
                  onReimport(selectedImportId);
                  handleMenuClose();
                }
              }}
            >
              <ListItemIcon>
                <ReplayIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Réimporter</ListItemText>
            </MenuItem>
          )}
          {onDelete && (
            <MenuItem 
              onClick={() => {
                if (selectedImportId && onDelete) {
                  onDelete(selectedImportId);
                  handleMenuClose();
                }
              }}
              sx={{ color: theme.palette.error.main }}
            >
              <ListItemIcon sx={{ color: theme.palette.error.main }}>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Supprimer</ListItemText>
            </MenuItem>
          )}
        </Menu>
      </Paper>
    </Box>
  );
};

export default ImportHistoryTable;